import React, { useMemo, useState, useEffect } from 'react';
import { Outlet, useNavigate, generatePath } from 'react-router-dom';
import {
    Typography,
    Space,
    Button,
    Flex,
    theme,
    Empty,
    type ColumnsType,
} from '@app/ui';
import { useTranslation } from 'react-i18next';
import ResizableTable from '@app/components/common/Table';
import {
    PlayCircleFilled,
    InfoCircleTwoTone,
    DownloadOutlined,
} from '@ant-design/icons';
import RouteNames from '../../../RouteNames';
import {
    useGetVerbalMentionsCsv,
    useSetUpVerbalMentionsTable,
    useVerbalMentionsTableStore,
    VM_PAGE_SIZE,
    generateVerbalMentionKey,
} from '../../hooks/useVerbalMentionsTable';
import { useBroadcastsContext } from '../../BroadcastsContext';
import { useRightsholderAttribution } from '@app/components/hooks/useRightsholderAttribution';
import { rhAttributionPostQueryFields } from '@app/reports/rhAttributionPostQueryFields';
import * as format from '@app/utils/formatters';
import connect from '@app/utils/connect';
import combineActions from '@app/utils/combineActions';
import sendNotification from '@app/actions/notifications/send';

const { Text } = Typography;

interface VerbalMentionColumnData {
    mentionKey: string;
    frameUrl: string | null;
    event: string;
    sponsor: string;
    keyWord: string;
    mvpPercentage: number;
    smv: number;
    network: string;
}

interface VerbalMentionsTableProps {
    user: {
        csvDelimiter: string;
    };
    sendNotification: (message: string, config?: any) => void;
}

const VerbalMentionsTable = ({
    user,
    sendNotification,
}: VerbalMentionsTableProps) => {
    const { t } = useTranslation('measurement');
    const navigate = useNavigate();
    const {
        token: { colorPrimary },
    } = theme.useToken();
    const { filters, client } = useBroadcastsContext();
    const { rightsholderAttribution } = useRightsholderAttribution({ client });
    const attributionQueryFields = useMemo(
        () => rhAttributionPostQueryFields(client, rightsholderAttribution),
        [client, rightsholderAttribution],
    );
    useSetUpVerbalMentionsTable(filters, attributionQueryFields);
    const [enableDownload, setEnableDownload] = useState(false);
    const { isLoading: isDownloadingCsv, isErrorDownloadingVerbalMentions } =
        useGetVerbalMentionsCsv(
            enableDownload,
            setEnableDownload,
            user.csvDelimiter || ',',
            filters,
            sendNotification,
            t,
            attributionQueryFields,
        );
    const {
        verbalMentionsData,
        isLoadingVerbalMentions,
        isFetchingVerbalMentions,
        currentPage,
        totalVerbalMentions,
        isErrorFetchingVerbalMentions,
        setCurrentPage,
    } = useVerbalMentionsTableStore();

    useEffect(() => {
        if (isErrorDownloadingVerbalMentions) {
            sendNotification(
                t(
                    'broadcastExposure.verbalMentions.table.notifications.errorDownloadingCsv',
                ),
                { context: 'danger' },
            );
        }
    }, [isErrorDownloadingVerbalMentions, t]);

    useEffect(() => {
        if (isErrorFetchingVerbalMentions) {
            sendNotification(
                t(
                    'broadcastExposure.verbalMentions.table.notifications.errorFetching',
                ),
                {
                    context: 'danger',
                },
            );
        }
    }, [isErrorFetchingVerbalMentions, t]);

    const handleVideoClick = (mentionKey: string) => {
        const mention = verbalMentionsData.get(mentionKey);
        if (!mention) return;
        const path = generatePath(RouteNames.verbalMentionModal.path, {
            broadcastName: encodeURIComponent(mention.broadcastName),
            contentDate: encodeURIComponent(mention.contentDate),
            mentionId: encodeURIComponent(mention.mentionId),
            networkId: encodeURIComponent(mention.network.id || ''),
            rightsholderId: encodeURIComponent(mention.rightsholder.id || ''),
            sponsorId: encodeURIComponent(mention.sponsor.id || ''),
        });
        navigate(path);
    };

    const handleDownloadCsv = () => {
        setEnableDownload(true);
    };

    const columns: ColumnsType<VerbalMentionColumnData> = [
        {
            title: t('broadcastExposure.verbalMentions.table.columns.media'),
            dataIndex: 'mentionKey',
            key: 'mentionKey',
            width: 120,
            fixed: 'left',
            render: (mentionKey: string, record: VerbalMentionColumnData) => {
                return (
                    <div
                        style={{
                            position: 'relative',
                            cursor: 'pointer',
                            borderRadius: '10px',
                            backgroundColor: 'rgba(0, 0, 0)',
                            border: '1px solid #00000040',
                            width: '100px',
                            height: '72px',
                        }}
                        onClick={() => handleVideoClick(mentionKey)}
                    >
                        {record.frameUrl && (
                            <img
                                src={record.frameUrl}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '9px',
                                }}
                            />
                        )}
                        <div
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 10,
                            }}
                        >
                            <PlayCircleFilled
                                style={{ color: '#F1F2F3', fontSize: '32px' }}
                            />
                        </div>
                    </div>
                );
            },
        },
        {
            title: t('broadcastExposure.verbalMentions.table.columns.event'),
            dataIndex: 'event',
            key: 'event',
            ellipsis: true,
            sorter: (a, b) => a.event.localeCompare(b.event),
            sortDirections: ['ascend', 'descend'],
            render: (text: string) => <Text>{text}</Text>,
        },
        {
            title: t('broadcastExposure.verbalMentions.table.columns.sponsor'),
            dataIndex: 'sponsor',
            key: 'sponsor',
            ellipsis: true,
            sorter: (a, b) => a.sponsor.localeCompare(b.sponsor),
            sortDirections: ['ascend', 'descend'],
            render: (text: string) => <Text>{text}</Text>,
        },
        {
            title: t('broadcastExposure.verbalMentions.table.columns.keyWord'),
            dataIndex: 'keyWord',
            key: 'keyWord',
            sorter: (a, b) => a.keyWord.localeCompare(b.keyWord),
            sortDirections: ['ascend', 'descend'],
            ellipsis: true,
            render: (text: string) => <Text>{text}</Text>,
        },
        {
            title: t(
                'broadcastExposure.verbalMentions.table.columns.mvpPercentage',
            ),
            dataIndex: 'mvpPercentage',
            key: 'mvpPercentage',
            sorter: (a, b) => a.mvpPercentage - b.mvpPercentage,
            sortDirections: ['descend', 'ascend'],
            render: (mvp: number) => <Text>{format.percentage(mvp)}</Text>,
        },
        {
            title: t('broadcastExposure.verbalMentions.table.columns.smv'),
            dataIndex: 'smv',
            key: 'smv',
            sorter: (a, b) => a.smv - b.smv,
            sortDirections: ['descend', 'ascend'],
            render: (smv: number) => (
                <Text>{format.shortCurrency(smv, 1)}</Text>
            ),
        },
        {
            title: t('broadcastExposure.verbalMentions.table.columns.network'),
            dataIndex: 'network',
            key: 'network',
            ellipsis: true,
            sorter: (a, b) => a.network.localeCompare(b.network),
            sortDirections: ['ascend', 'descend'],
            render: (text: string) => <Text>{text}</Text>,
        },
    ];

    const tableData: VerbalMentionColumnData[] = Array.from(
        verbalMentionsData.values(),
    ).map(vm => ({
        mentionKey: generateVerbalMentionKey(vm),
        frameUrl: vm.frameUrl,
        event: vm.broadcastName,
        sponsor: vm.sponsor.name,
        keyWord: vm.mention,
        mvpPercentage: vm.averageMvpScore,
        smv: vm.sponsorMediaValue,
        network: vm.network.name,
    }));

    return (
        <div>
            <Space direction="vertical" style={{ width: '100%' }}>
                <Flex
                    align="center"
                    justify="end"
                    gap="8px"
                    style={{ marginBottom: '32px' }}
                >
                    <Text style={{ color: '#2E3646' }}>
                        {t('broadcastExposure.verbalMentions.table.exportInfo')}
                    </Text>
                    <InfoCircleTwoTone
                        twoToneColor={colorPrimary}
                        style={{ fontSize: '24px' }}
                    />
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={handleDownloadCsv}
                        disabled={
                            verbalMentionsData.size === 0 ||
                            totalVerbalMentions === 0 ||
                            isLoadingVerbalMentions ||
                            isDownloadingCsv
                        }
                    >
                        {t(
                            'broadcastExposure.verbalMentions.table.downloadFullData',
                        )}
                    </Button>
                </Flex>
                <ResizableTable
                    columns={columns}
                    dataSource={tableData}
                    rowKey="mentionKey"
                    loading={
                        isLoadingVerbalMentions ||
                        (isFetchingVerbalMentions && tableData.length === 0)
                    }
                    pagination={{
                        pageSize: VM_PAGE_SIZE,
                        showSizeChanger: false,
                        current: currentPage + 1,
                        total: totalVerbalMentions,
                        onChange: page => {
                            setCurrentPage(page - 1);
                        },
                    }}
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: (
                            <Empty
                                description={t(
                                    'broadcastExposure.verbalMentions.table.emptyState',
                                )}
                            />
                        ),
                    }}
                    rowClassName={(_, index) =>
                        index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
                    }
                    bordered
                    size="middle"
                />
            </Space>
            <Outlet />
        </div>
    );
};

export default connect(
    ['user'],
    combineActions({ sendNotification }),
)(VerbalMentionsTable);
